import { trackEvent } from '@/common/store/pinia/telemetry/core/client';
import { toSeconds } from '@/common/store/pinia/telemetry/core/utils';
import type { TTIInfo, LongTaskEntry } from '@/common/store/pinia/telemetry/core/types';
import { trackDomContentLoaded, trackFullyLoadedTime } from '@/common/store/pinia/telemetry/modules/performance';

const MAX_LONG_TASKS = 100;
const MAX_RETRIES = 10;
const QUIET_WINDOW_MS = 5000;

let longTaskObserver: PerformanceObserver | null = null;
let longTasks: LongTaskEntry[] = [];
let ttiCalculated = false;
let retryCount = 0;
let domContentLoadedTracked = false;
let fullyLoadedTracked = false;

export const trackTimeToInteractive = (
  ttiValue: number,
  additionalProperties?: Record<string, unknown>
): void => {
  trackEvent('time_to_interactive', {
    tti_ms: Math.round(ttiValue),
    tti_seconds: toSeconds(ttiValue),
    ...additionalProperties
  });
};

export const trackLongTask = (
  taskInfo: LongTaskEntry,
  additionalProperties?: Record<string, unknown>
): void => {
  trackEvent('long_task_detected', {
    task_name: taskInfo.name,
    duration_ms: Math.round(taskInfo.duration),
    start_time: Math.round(taskInfo.startTime),
    entry_type: taskInfo.entryType,
    ...additionalProperties
  });
};

type NavigationTimingSource = PerformanceNavigationTiming | PerformanceTiming;

const isNavigationTimingEntry = (timing: NavigationTimingSource): timing is PerformanceNavigationTiming => 'entryType' in timing;

const getNavigationTiming = (): NavigationTimingSource | null => {
  try {
    const entries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    if (entries && entries.length > 0) {
      return entries[0];
    }
  } catch (error) {
    // Ignore and fall back to deprecated API
  }

  const { timing } = performance;
  if (timing && typeof timing.navigationStart === 'number' && timing.navigationStart > 0) {
    return timing;
  }

  return null;
};

const toRelativeTime = (timing: NavigationTimingSource, value?: number): number | null => {
  if (typeof value !== 'number' || value <= 0 || Number.isNaN(value)) {
    return null;
  }

  if (isNavigationTimingEntry(timing)) {
    return value;
  }

  const { navigationStart } = timing;
  if (typeof navigationStart !== 'number' || navigationStart <= 0) {
    return null;
  }

  const relative = value - navigationStart;
  return relative >= 0 ? relative : null;
};

const getNormalizedNavigationStart = (timing: NavigationTimingSource): number | null => {
  if (isNavigationTimingEntry(timing)) {
    return typeof timing.startTime === 'number' ? timing.startTime : 0;
  }

  return 0;
};

const findQuietWindow = (searchStart: number, tasks: LongTaskEntry[]): number | null => {
  const currentTime = performance.now();

  if (currentTime - searchStart < QUIET_WINDOW_MS) {
    return null;
  }

  const sortedTasks = tasks
    .filter(task => task.startTime >= searchStart)
    .sort((a, b) => a.startTime - b.startTime);

  if (sortedTasks.length === 0) {
    return searchStart;
  }

  if (sortedTasks[0].startTime - searchStart >= QUIET_WINDOW_MS) {
    return searchStart;
  }

  for (let i = 0; i < sortedTasks.length - 1; i++) {
    const currentTaskEnd = sortedTasks[i].startTime + sortedTasks[i].duration;
    const nextTaskStart = sortedTasks[i + 1].startTime;

    if (nextTaskStart - currentTaskEnd >= QUIET_WINDOW_MS) {
      return currentTaskEnd;
    }
  }

  const lastTask = sortedTasks[sortedTasks.length - 1];
  const lastTaskEnd = lastTask.startTime + lastTask.duration;

  if (currentTime - lastTaskEnd >= QUIET_WINDOW_MS) {
    return lastTaskEnd;
  }

  return null;
};

const calculateTTI = (): TTIInfo | null => {
  try {
    const [navigationEntry] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];

    if (!navigationEntry) {
      return null;
    }

    const {
      startTime,
      domContentLoadedEventEnd,
      loadEventEnd
    } = navigationEntry;

    if (!domContentLoadedEventEnd || domContentLoadedEventEnd <= 0) {
      return null;
    }

    const searchStart = domContentLoadedEventEnd;
    const quietWindow = findQuietWindow(searchStart, longTasks);
    const tti = quietWindow !== null ? Math.min(quietWindow, loadEventEnd || Infinity) : (loadEventEnd || searchStart);

    return {
      value: tti,
      timestamp: Date.now(),
      navigationStart: startTime,
      domContentLoadedEnd: domContentLoadedEventEnd,
      loadEventEnd: loadEventEnd || 0
    };
  } catch (error) {
    return null;
  }
};

const trackDomContentLoadedMetric = (): boolean => {
  if (domContentLoadedTracked) {
    return true;
  }

  const timing = getNavigationTiming();
  if (!timing) {
    return false;
  }

  let domContentLoadedEnd = toRelativeTime(timing, timing.domContentLoadedEventEnd);
  if (domContentLoadedEnd === null) {
    const fallbackNow = typeof performance !== 'undefined' && typeof performance.now === 'function'
      ? performance.now()
      : null;

    if (fallbackNow === null) {
      return false;
    }

    domContentLoadedEnd = fallbackNow;
  }

  const domContentLoadedStart = toRelativeTime(timing, timing.domContentLoadedEventStart);
  const navigationStart = getNormalizedNavigationStart(timing);

  trackDomContentLoaded({
    duration: domContentLoadedEnd,
    domContentLoadedStart,
    domContentLoadedEnd,
    navigationStart
  });

  domContentLoadedTracked = true;
  return true;
};

const trackFullyLoadedMetric = (): boolean => {
  if (fullyLoadedTracked) {
    return true;
  }

  const timing = getNavigationTiming();
  if (!timing) {
    return false;
  }

  let loadEventEnd = toRelativeTime(timing, timing.loadEventEnd);
  if (loadEventEnd === null) {
    const fallbackNow = typeof performance !== 'undefined' && typeof performance.now === 'function'
      ? performance.now()
      : null;

    if (fallbackNow === null) {
      return false;
    }

    loadEventEnd = fallbackNow;
  }

  const loadEventStart = toRelativeTime(timing, timing.loadEventStart);
  const navigationStart = getNormalizedNavigationStart(timing);

  trackFullyLoadedTime({
    duration: loadEventEnd,
    loadEventStart,
    loadEventEnd,
    navigationStart
  });

  fullyLoadedTracked = true;
  return true;
};

const scheduleDomContentLoadedTracking = (): void => {
  if (typeof document === 'undefined') {
    return;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      trackDomContentLoadedMetric();
    }, { once: true });
    return;
  }

  if (!trackDomContentLoadedMetric() && document.readyState !== 'complete') {
    document.addEventListener('DOMContentLoaded', () => {
      trackDomContentLoadedMetric();
    }, { once: true });
  }
};

export const trackTTIOnPageLoad = (): void => {
  if (ttiCalculated) {
    return;
  }

  const ttiInfo = calculateTTI();

  if (!ttiInfo && retryCount < MAX_RETRIES) {
    retryCount++;
    setTimeout(() => {
      if (!ttiCalculated) {
        trackTTIOnPageLoad();
      }
    }, 1000);
    return;
  }

  if (ttiInfo) {
    ttiCalculated = true;
    const { value, navigationStart, domContentLoadedEnd, loadEventEnd } = ttiInfo;

    trackTimeToInteractive(value, {
      navigation_start: navigationStart,
      dom_content_loaded_end: domContentLoadedEnd,
      load_event_end: loadEventEnd
    });
  }
};

const scheduleFullyLoadedTracking = (): void => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  const handleLoad = () => {
    trackFullyLoadedMetric();
    trackTTIOnPageLoad();
  };

  if (document.readyState === 'complete') {
    handleLoad();
    return;
  }

  window.addEventListener('load', handleLoad, { once: true });
};

export const initializeLongTaskDetection = (): void => {
  if (!window.PerformanceObserver || longTaskObserver) {
    return;
  }

  let testObserver: PerformanceObserver | null = null;
  try {
    testObserver = new PerformanceObserver(() => {});
    testObserver.observe({ entryTypes: ['longtask'] });
    testObserver.disconnect();
  } catch (error) {
    if (testObserver) {
      testObserver.disconnect();
    }
    return;
  }

  try {
    longTaskObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        if (entry.duration >= 50) {
          const longTaskData: LongTaskEntry = {
            name: entry.name || 'unknown',
            duration: entry.duration,
            startTime: entry.startTime,
            entryType: entry.entryType
          };

          if (longTasks.length >= MAX_LONG_TASKS) {
            longTasks.shift();
          }
          longTasks.push(longTaskData);

          trackLongTask(longTaskData);
        }
      });
    });

    longTaskObserver.observe({ entryTypes: ['longtask'] });
  } catch (error) {
    longTaskObserver = null;
  }
};

export const cleanupTTITracking = (): void => {
  if (longTaskObserver) {
    longTaskObserver.disconnect();
    longTaskObserver = null;
  }

  longTasks = [];
  ttiCalculated = false;
  retryCount = 0;
  domContentLoadedTracked = false;
  fullyLoadedTracked = false;
};

export const initializeTTITracking = (): void => {
  initializeLongTaskDetection();
  scheduleDomContentLoadedTracking();
  scheduleFullyLoadedTracking();
};
